import { graphql } from 'gatsby';
import React from 'react';
import Seo from '../../components/SEO';
import ConsultantCard from '../../components/ConsultantCard/ConsultantCard';
import ConsultantCover from '../../components/ConsultantCover/ConsultantCover';
import ConsultantDetails from '../../components/ConsultantDetails/ConsultantDetails';
import HeaderTheme from '../../components/Header/HeaderTheme';
import Services from '../../components/Services/Services';
import BackLink from '../../components/UI/BackLink/BackLink';
import { GridReveal, GridItem } from '../../components/UI/Grid/Grid';
import Section from '../../components/UI/Section/Section';

const ConsultantTemplate = ({
  data: {
    page: { title, consultantDetails, sectionContent, specialityTags, metadata, seo },
    relatedConsultants,
  },
}) => {
  const specialities = specialityTags.nodes?.map(({ name }) => name).join(', ');

  return (
    <>
      <HeaderTheme isReverse/>

      <Seo 
        title={`Expert: ${seo.title}`}
        description={metadata.metaDescription || specialities}
        image={metadata.metaImage || consultantDetails.photo}
        seo={seo}
      />

      <ConsultantCover
        teaser={consultantDetails.jobTitle ? consultantDetails.jobTitle : `Expert`}
        heading={title}
        specialities={specialities}
        qualifications={consultantDetails.qualifications}
        photo={consultantDetails.photo}
      />

      <ConsultantDetails {...consultantDetails} body={sectionContent.body} />

      <Services
        heading="Related **services**"
        nodes={consultantDetails.relatedServices}
        isInnerOffsetY={false}
      />

      {!!relatedConsultants.nodes?.length && (
        <Section heading="Related **experts**">
          <GridReveal>
            {relatedConsultants.nodes?.map(
              ({ id, title, uri, consultantDetails, specialityTags }) => {
                return (
                  <GridItem md={4} lg={3} xl={3} key={id}>
                    <ConsultantCard
                      {...consultantDetails}
                      title={title}
                      specialities={specialityTags}
                      path={uri}
                    />
                  </GridItem>
                );
              }
            )}
          </GridReveal>
        </Section>
      )}

      <BackLink label="experts" path="/experts"/>
    </>
  );
};

export default ConsultantTemplate;

export const query = graphql`
  query consultant($id: String!, $specialityIds: [String]) {
    page: wpConsultant(id: {eq: $id}) {
      nodeType
      title
      uri
      seo {
          title
          metaDesc
          focuskw
          metaKeywords
          metaRobotsNoindex
          metaRobotsNofollow
          opengraphTitle
          opengraphDescription
          opengraphImage {
              altText
              sourceUrl
              srcSet
          }
          twitterTitle
          twitterDescription
          twitterImage {
              altText
              sourceUrl
              srcSet
          }
          canonical
          cornerstone
          schema {
              articleType
              pageType
              raw
          }
      }
      metadata {
        metaDescription
        metaImage {
          ...WpImage
        }
      }
      consultantDetails {
        jobTitle
        qualifications
        gmcNumber
        photo {
          localFile {
            childImageSharp {
              gatsbyImageData(
                width: 430
                height: 430
                breakpoints: [430]
                placeholder: BLURRED
                transformOptions: {grayscale: true}
                layout: CONSTRAINED
              )
            }
          }
        }
        ratingLink
        relatedServices {
          ... on WpService {
            id
            title
            uri
            cover: coverService {
              lead
              image {
                localFile {
                  ...HalfImage
                }
              }
            }
          }
        }
        location {
          ... on WpLocation {
            id
            title
            uri: uriRedirect
            locationDetails {
              address
              telephone
              gallery {
                ...WpImage
              }
            }
          }
        }
      }
      sectionContent {
        body {
          ... on WpConsultant_Sectioncontent_Body_LayoutWysiwyg {
            fieldGroupName
            wysiwyg
          }
          ... on WpConsultant_Sectioncontent_Body_LayoutImage {
            fieldGroupName
            image {
              title
              ...WpImage
            }
          }
          ... on WpConsultant_Sectioncontent_Body_LayoutVideo {
            fieldGroupName
            caption
            video
          }
        }
      }
      specialityTags {
        nodes {
          id
          name
        }
      }
    }
    relatedConsultants: allWpConsultant(
      filter: {id: {ne: $id}, specialityTags: {nodes: {elemMatch: {id: {in: $specialityIds}}}}}
      limit: 4
    ) {
      nodes {
        ...ConsultantCard
      }
    }
  }
`;
